import Link from 'gatsby-link';
import React, { Component } from 'react';
import {
  DISTRICT_REQUIREMENTS, FEDERAL_EXEMPTION_STATES,
  STATES, legalServiceProviders,
} from '@upsolve/shared';
import { format } from 'date-fns';
import { get, padStart, startCase } from 'lodash';

import Article from '../components/Layout/Article.div';
import AuthorsBlock from '../components/Layout/AuthorsBlock';
import BreadcrumbNav from '../components/Layout/BreadcrumbNav';
import CTASideBySideOfferings from '../components/CTA/CTASideBySideOfferings';
import CTATestimonial from '../components/Programmatic/CTATestimonial';
import ContentShareRow from '../components/Share/ContentShareRow';
import CourthouseImageMap from '../../static/images/courthouses-images-filelist.json';
import HeroFilerCta from '../components/CTA/HeroFilerCta';
import HorizontalRule from '../components/Layout/HorizontalRule.hr';
import ImageStaticMap from '../components/Media/ImageStaticMap';
import MainFooter from '../components/Layout/Footer'
import MainHeader from '../components/Layout/Header'
import P from '../components/Type/Paragraph.p';
import SCREENER_CTA_TYPES from '../components/analytics/screenerCtaTypes';
import SEO from '../components/SEO/SEO';
import StateCityGuidesLinks from '../components/Programmatic/StateCityGuidesLinks';
import StateMedianIncomeChart from '../components/Programmatic/StateMedianIncomeChart';
import StatePovertyLevelsChart from '../components/Programmatic/StatePovertyLevelsChart';
import TopLevelLayout from '../components/Layout/TopLevelLayout';
import UpsolveFilersCount from '../components/ProofBuilders/UpsolveFilersCount';
import VideoInContext from '../components/Media/VideoInContext';
import courts from '../../content/courts.json';
import getScreenerUrl from '../components/Links/getScreenerUrl';
import privatePricings from '../../content/privatePricings.json';
import trackPageView from '../components/analytics/trackPageView';
import { Ampify } from '../components/AMP/AmpContext';
import { H1, H2, H3, H5, H6 } from '../components/Type/Display.h';

import * as CONTENT_CREATION_METHODS from '../components/analytics/contentCreationMethods';
import * as CONTENT_GROUPS from '../components/analytics/contentGroups';
import * as CONTENT_TOPICS from '../components/analytics/contentTopics';
import * as SEARCH_TERM_TARGETS from '../components/analytics/searchTermTargets';

const contentGroup = CONTENT_GROUPS.CITY_PAGE;

export default class CityPageTemplate extends Component {
  componentDidMount() {
    trackPageView({
      contentCreationMethod: CONTENT_CREATION_METHODS.PROGRAMMATIC,
      contentGroup,
      contentTopics: [CONTENT_TOPICS.FILING_ON_MY_OWN],
      contentLocalized: true,
      searchTermTarget: SEARCH_TERM_TARGETS.HOW_TO_FILE_BANKRUPTCY,
    });
  }

  render() {
    const { path } = this.props;
    const { amp, city, created, district, modified, state } = this.props.pageContext;
    const districtRequirements = DISTRICT_REQUIREMENTS[district.name];
    const title = `How to File Bankruptcy in ${startCase(city.city)}, ${state}${(city.zipcodes || []).length === 1 ? ` ${padStart(city.zipcodes[0], 5, '0')}`: ''} for Free ${format(new Date(), 'yyyy')}`;
    const description = `Want to file Chapter 7 bankruptcy? Upsolve is a legal aid nonprofit that provides free Chapter 7 bankruptcy attorneys to debtors in ${startCase(city.city)}, ${state} that want to erase debt.`;
    const cta = `File Bankruptcy in ${startCase(city.city)}`;
    const screenerUrl = getScreenerUrl({ cta: SCREENER_CTA_TYPES.CITY_PAGE, contentGroup });

    const children = (
      <TopLevelLayout showPersistentCta persistentCtaText={cta}>
        <SEO
          title={title}
          description={description}
          includeRating
          jsonLD={[
            {
              '@context': 'http://schema.org',
              '@type': 'HowTo',
              name: `How to File Bankruptcy for Free in ${startCase(city.city)} (${format(new Date(), 'yyyy')} Guide)`,
              estimatedCost: {
                '@type': 'MonetaryAmount',
                currency: 'USD',
                value: '0',
              },
              totalTime: 'PT3H',
              prepTime: 'PT30M',
              performTime: 'PT2H30M',
              step: [
                {
                  '@type': 'HowToSection',
                  name: `Collect Your ${startCase(city.city)} Documents`,
                  position: '1',
                  itemListElement: [
                    {
                      '@type': 'HowToStep',
                      position: '1',
                      itemListElement: [
                        {
                          '@type': 'HowToDirection',
                          position: '1',
                          text: 'Collect all your financial documents.'
                        },
                      ]
                    },
                  ]
                },
                {
                  '@type': 'HowToSection',
                  name: 'Take Credit Counseling',
                  position: '2',
                  itemListElement: [
                    {
                      '@type': 'HowToStep',
                      position: '1',
                      itemListElement: [
                        {
                          '@type': 'HowToDirection',
                          position: '1',
                          text: 'Take a credit counseling course that is approved by the Department of Justice.'
                        },
                      ]
                    },
                  ]
                },
                {
                  '@type': 'HowToSection',
                  name: 'Complete the Bankruptcy Forms',
                  position: '3',
                  itemListElement: [
                    {
                      '@type': 'HowToStep',
                      position: '1',
                      itemListElement: [
                        {
                          '@type': 'HowToDirection',
                          position: '1',
                          text: 'Print out the forms online. Fill them out.'
                        },
                        {
                          '@type': 'HowToTip',
                          position: '2',
                          text: 'You will have to enter repetitive data and make lots of math calculations.'
                        },
                      ]
                    },
                  ]
                },
                {
                  '@type': 'HowToSection',
                  name: 'Get Your Filing Fee',
                  position: '4',
                  itemListElement: [
                    {
                      '@type': 'HowToStep',
                      position: '1',
                      itemListElement: [
                        {
                          '@type': 'HowToDirection',
                          position: '1',
                          text: 'Gather $335 to pay the court or pay with installments.'
                        },
                        {
                          '@type': 'HowToTip',
                          position: '2',
                          text: 'You can submit a form to apply for a fee waiver.'
                        },
                      ]
                    },
                  ]
                },
                {
                  '@type': 'HowToSection',
                  name: 'Print Your Bankruptcy Forms and Bring them to Court',
                  position: '5',
                  itemListElement: [
                    {
                      '@type': 'HowToStep',
                      position: '1',
                      itemListElement: [
                        {
                          '@type': 'HowToDirection',
                          position: '1',
                          text: 'Once you have prepared your bankruptcy forms, you will need to print them out for the court.'
                        },
                        {
                          '@type': 'HowToTip',
                          position: '2',
                          text: 'Most bankruptcy courts require just 1 copy of the petition, but some courts require 4 copies.'
                        },
                      ]
                    },
                  ]
                },
                {
                  '@type': 'HowToSection',
                  name: 'Go to Court to File Your Forms',
                  position: '6',
                  itemListElement: [
                    {
                      '@type': 'HowToStep',
                      position: '1',
                      itemListElement: [
                        {
                          '@type': 'HowToDirection',
                          position: '1',
                          text: 'Bring your bankruptcy forms and Course 1 certificate to the court to file.'
                        },
                        {
                          '@type': 'HowToTip',
                          position: '2',
                          text: 'The court is open from Monday through Friday excluding federal holidays during normal business hours.'
                        },
                      ]
                    },
                  ]
                },
                {
                  '@type': 'HowToSection',
                  name: 'Mail Documents to Your Trustee',
                  position: '7',
                  itemListElement: [
                    {
                      '@type': 'HowToStep',
                      position: '1',
                      itemListElement: [
                        {
                          '@type': 'HowToDirection',
                          position: '1',
                          text: 'Mail the trustee certain financial documents, like tax returns, pay stubs, and bank statements.'
                        },
                        {
                          '@type': 'HowToTip',
                          position: '2',
                          text: 'If you don’t mail the the trustee the requested documents, you will not get a discharge of your debts.'
                        },
                      ]
                    },
                  ]
                },
                {
                  '@type': 'HowToSection',
                  name: 'Take Bankruptcy Course 2',
                  position: '8',
                  itemListElement: [
                    {
                      '@type': 'HowToStep',
                      position: '1',
                      itemListElement: [
                        {
                          '@type': 'HowToDirection',
                          position: '1',
                          text: 'After filing your bankruptcy forms, you also need to take your second mandatory bankruptcy course'
                        },
                      ]
                    },
                  ]
                },
                {
                  '@type': 'HowToSection',
                  name: 'Attend Your 341 Meeting',
                  position: '9',
                  itemListElement: [
                    {
                      '@type': 'HowToStep',
                      position: '1',
                      itemListElement: [
                        {
                          '@type': 'HowToDirection',
                          position: '1',
                          text: 'Attend your 341 meeting'
                        },
                        {
                          '@type': 'HowToTip',
                          position: '2',
                          text: 'The location of the 341 Meeting depends on where you filed your bankruptcy case.'
                        },
                      ]
                    },
                  ]
                },
                {
                  '@type': 'HowToSection',
                  name: 'Get a Fresh Start',
                  position: '10',
                  itemListElement: [
                    {
                      '@type': 'HowToStep',
                      position: '1',
                      itemListElement: [
                        {
                          '@type': 'HowToDirection',
                          position: '1',
                          text: 'Begin the new chapter to your life!'
                        },
                      ]
                    },
                  ]
                },
              ],
            },
            {
              '@context': 'https://schema.org',
              '@type': 'VideoObject',
              name: `How to File Bankruptcy in ${startCase(city.city)}, ${STATES[city.state]} for Free (${format(new Date(), 'yyyy')})`,
              description,
              thumbnailUrl: [
                'https://static.upsolve.org/images/thumbnails/how-to-file-bankruptcy-1x1.jpg',
                'https://static.upsolve.org/images/thumbnails/how-to-file-bankruptcy-4x3.jpg',
                'https://static.upsolve.org/images/thumbnails/how-to-file-bankruptcy-16x9.jpg',
              ],
              uploadDate: new Date(created).toISOString(),
              duration: 'PT1M40S',
              contentUrl: 'https://www.youtube.com/watch?v=v74E7K9N69E',
              embedUrl: 'https://www.youtube.com/embed/v74E7K9N69E',
            }
          ]}
        />
        <MainHeader contentGroup={contentGroup} />
        <Article>
          <BreadcrumbNav />
          <div className="article__body">
            <article className="article__content">
              <div className="content-hero">
                <header className="content-hero__header">
                  <H1>{startCase(city.city)}, {STATES[city.state]}{(city.zipcodes || []).length === 1 ? ` ${padStart(city.zipcodes[0], 5, '0')}`: ''} Bankruptcy</H1>
                  <UpsolveFilersCount />
                  <HorizontalRule />
                  <AuthorsBlock
                    createdAt={created}
                    updatedAt={modified}
                  />
                </header>
              </div>
              <div className="content-body">
                <div className="content-body__container">
                  <nav>
                    <ol className="content-body__nav">
                      <li><a href="#how-to-file-bankruptcy-for-free">How to File Bankruptcy in {startCase(city.city)}, {STATES[city.state]} {(city.zipcodes || []).length === 1 ? ` ${padStart(city.zipcodes[0], 5, '0')}`: ''} for Free</a></li>
                      <li><a href="#means-test">Means Test for {startCase(city.city)}, {STATES[city.state]}</a></li>
                      <li><a href="#forms">Bankruptcy Forms for {startCase(city.city)}, {STATES[city.state]}</a></li>
                      <li><a href="#exemptions">Bankruptcy Exemptions for {startCase(city.city)}, {STATES[city.state]}</a></li>
                      <li><a href="#cost-estimate">{startCase(city.city)}, {STATES[city.state]} Bankruptcy Attorney Cost Estimate</a></li>
                    </ol>
                  </nav>

                  <section className="content-video">
                    <VideoInContext
                      video={{ youtubeId: 'v74E7K9N69E' }}
                    />
                  </section>

                  <section>
                    {CourthouseImageMap.includes(`${city.city.replace(/\.|\s/g, '').toLowerCase()}-${state.toLowerCase().replace(/\s/g, '')}-bankruptcy-courthouse.png`) && <div className="cover-image">
                      <img src={`/images/courthouses/${city.city.replace(/\.|\s/g, '').toLowerCase()}-${state.toLowerCase().replace(/\s/g, '')}-bankruptcy-courthouse.png`} alt={`${startCase(city.city)} ${city.state} bankruptcy courthouse`} />
                    </div>}
                    <p>Chapter 7 bankruptcy is an important debt relief tool for Americans in severe financial distress from losing a job, getting injured, or getting divorced.</p>
                    <p>A good bankruptcy lawyer  is a great resource whenever considering <Link to="/learn/chapter-7-versus-chapter-13-bankruptcy/">filing for Chapter 7</Link>.  Unfortunately, many low-income {startCase(city.city)} residents who need a fresh start cannot afford the $1,500 that it often costs to hire an attorney.  Our nonprofit <Link to="/">Upsolve</Link> was founded in Harvard Law’s Access to Justice Lab to help low-income Americans who need a fresh start.</p>
                    <p>If you need a fresh start in {startCase(city.city)} and cannot afford a lawyer, this article is for you.</p>
                    <p>We will first explain the pros and cons of filing.  If Chapter 7 is a good fit, we will give you a 10 step checklist to follow for filing in the <a href={district.url} target="_blank" rel="noopener noreferrer">{district.name}</a>.</p>
                    <p>Whether or not you choose to file Chapter 7 using our free nonprofit <Link to="/">Upsolve.org</Link>, this article will leave you much better prepared to obtain a better financial future.</p>
                  </section>

                  <section>
                    <H3>Before Starting...</H3>
                    <p>First, you need to know whether you need to file for bankruptcy. You may not need to file bankruptcy. Chapter 7 bankruptcy is a very effective tool for erasing credit card bills and medical debt. But you can only be use it once every 8 years.</p>

                    <p>So it often doesn’t make sense to file unless you know that you’re going to be able to discharge a significant amount of debt, at least $10,000.  And you’re confident that all of our property is protected by <Link to="/legal-definitions/exemptions/">exemptions</Link>. If not, you should diligently explore <Link to="/learn/alternative-to-chapter-7-bankruptcy/">alternatives to Chapter 7</Link>.</p>
                    <p>That said, <a href="https://www.abi.org/abi-journal/chapter-7-asset-cases" target="_blank" rel="noopener noreferrer">filers in 96% of Chapter 7 cases</a> get to keep everything they own. And research shows that waiting to file for bankruptcy too long can be <a href="http://ndlawreview.org/wp-content/uploads/2018/11/4-Foohey-et-al..pdf" target="_blank" rel="noopener noreferrer">very hazardous to people’s finances</a>.</p>
                    <p>If you prefer to get help of an attorney in your bankruptcy case, you can find listings of local bankruptcy lawyers from the National Association of Consumer Bankruptcy Attorneys. A good bankruptcy lawyer should be able to advise you on how to maximize the economic value of your bankruptcy filing. On the other hand, if you simply have too much credit card debt you may be able to do it yourself.  And if are very low-income, you may also be able to get help from a <Link to="/learn/how-to-get-free-legal-aid-help/">legal aid organization</Link> in your area.</p>

                    {legalServiceProviders.filter(lsp => lsp.state === state).length > 0 && <p>The contact information for the legal aid organizations closest to you is:</p>}
                    {Array.prototype.map.call(legalServiceProviders.filter(lsp => lsp.state === state), (la) => (
                      <div key={la.name}>
                        <p>
                          <a href={la.url || ''} target="_blank" rel="noopener noreferrer">{la.name}</a><br />
                          {la.phone}<br />
                          {la.address}
                          <ImageStaticMap alt={`${la.name} Location`} address={la.address} />
                        </p>
                      </div>
                    ))}
                  </section>

                  <section>
                    <H2 id="how-to-file-bankruptcy-for-free">How to File Bankruptcy in {startCase(city.city)}, {STATES[city.state]} {(city.zipcodes || []).length === 1 ? ` ${padStart(city.zipcodes[0], 5, '0')}`: ''} for Free</H2>
                    <br />
                    <H3 id="1collectyourdocuments">1. Collect Your Documents</H3>
                    <p>Before getting started, you need to collect all your financial documents so you understand the current state of your finances.  </p>
                    <p>First, you need to obtain a copy of your credit report from Experian, Transunion, or Equifax to learn how much debt you owe. You can obtain your credit report from all three at AnnualCreditReport.com.</p>
                    <p>Some of your debts may not be listed on your credit report, like medical bills, personal loans, or tax debts.  Make a list of any missing debts as you will need to list all of them on your bankruptcy forms.</p>
                    <p>In addition to your credit report, you will need the following documents:</p>

                    <ul>
                      <li>Tax returns for the past 2 years</li>
                      <li>Pay stubs or other proof of your income for the last 6 months</li>
                      <li>Recent bank account statements</li>
                      <li>Recent retirement account or brokerage account statements</li>
                      <li>Valuations or appraisals of any real estate you own</li>
                      <li>Copies of vehicle registration</li>
                      <li>Any Other Documents relating to Your Assets, Debts, or Income.</li>
                    </ul>
                    <p>Having these documents next to you will help you get an accurate picture of your finances.</p>

                    <H3 id="2takecreditcounseling">2. Take Credit Counseling</H3>
                    <p>An important first step to the bankruptcy process is credit counseling. Everyone who files for bankruptcy is required to take a <Link to="/learn/bankruptcy-counseling-courses/">credit counseling course</Link> that is approved by the <a href="https://www.justice.gov/ust/list-credit-counseling-agencies-approved-pursuant-11-usc-111" target="_blank" rel="noopener noreferrer">Department of Justice</a>.</p>
                    <p>Credit counseling courses <a href="https://debtorcc.org/first-bankruptcy-course.aspx" target="_blank" rel="noopener noreferrer">like this one</a> give you an idea of whether you really need to file for bankruptcy or whether you could get back on your feet through some type of informal repayment plan.</p>
                    <p>You will provide the credit counseling agency with your income and expenses. Together, you will review the options for repaying the debt, like <Link to="/learn/do-i-need-debt-consolidation/">debt consolidation</Link>, or <Link to="/learn/debt-settlement">debt settlement</Link>.</p>
                    <p>In many cases, this exercise only confirms that you don't have any feasible options for addressing the debt other than bankruptcy. But it’s a valuable exercise even still.</p>
                    <p>The course takes at least one hour and can be completed online or by telephone.  The course fee ranges from $10 to $50, depending on the provider. But if your household income is under 150% of the federal poverty line, you should be able to get this fee waived.</p>
                    <p>Once you complete the course, you will receive a certificate of completion.  Keep it.  You will need to give a copy of this certificate to the court when you file your bankruptcy forms in Step 5</p>

                    <H3 id="3completethebankruptcyforms">3. Complete the Bankruptcy Forms</H3>
                    <p>This is the most time-consuming step. The Bankruptcy Forms include <Link to={`/${city.state.toLowerCase()}/bankruptcy-forms/`}>23 seperate forms</Link> totalling roughly 70 pages. The forms ask you about everything you make, spend, own and owe.</p>
                    <p>If you download and print out the forms online, you will have to enter repetitive data and make lots of math calculations.</p>
                    <p>So if you’re not able to hire a lawyer, you probably want to buy a bankruptcy software program or use <a href={screenerUrl}>Upsolve.org’s</a> free online bankruptcy service.</p>

                    <H3 id="4getyourfilingfee">4. Get Your Filing Fee</H3>
                    <p>Filing for Chapter 7 bankruptcy normally requires a <Link to="/learn/have-to-pay-335-dollars-filing-fee/">$335 filing fee</Link>, which must be paid to the court in person in exact change.  </p>
                    <p>If you don’t have the funds to pay the filing fee now, you can complete a <a href="http://www.uscourts.gov/sites/default/files/b_3a_0.pdf" target="_blank" rel="noopener noreferrer">special form</a>, asking to pay your fee in installments. You can ask to pay the $335 fee in up to 4 payments within 120 days of your filing date.  </p>
                    <p>If paying in installments isn’t even possible, you can submit <a href="http://www.uscourts.gov/sites/default/files/form_b103b.pdf" target="_blank" rel="noopener noreferrer">another form</a> to apply for a fee waiver. To qualify, your total household income must be under <a href="http://www.uscourts.gov/sites/default/files/poverty-guidelines.pdf" target="_blank" rel="noopener noreferrer">150% of the federal poverty line</a>. The court will decide whether you get a fee waiver after you file. If your application is denied, the court will order you to pay the fee in installments.</p>

                    <H3 id="5printyourbankruptcyformsandbringthemtocourt">5. Print Your Bankruptcy Forms and Bring them To Court</H3>
                    <p>Once you have prepared your bankruptcy forms, you will need to print them out for the court. <em>You must print them single-sided.</em> The court won’t accept double-sided pages.</p>
                    <p>You will also need to sign the forms once they are printed.  </p>
                    <p>Most bankruptcy courts require just 1 copy of the petition, but some courts like the <a href="http://www.nysb.uscourts.gov/" target="_blank" rel="noopener noreferrer">bankruptcy court in Manhattan</a> require 4 copies. So call your local bankruptcy court to find out how many copies you will need to bring.</p>

                    <H3 id="6gotocourttofileyourforms">6. Go to Court to File Your Forms</H3>

                    <p>It’s now time to bring the bankruptcy forms and your Course 1 certificate to the court to file them.</p>
                    <p>The court is open from Monday through Friday excluding federal holidays during normal business hours. Call ahead of time if you want to know when it is open.  If you have questions about how to get to the court, you can call the court staff.</p>
                    {Array.prototype.map.call(courts.filter(court => get(court, 'address.state') === city.state), (court) => (
                      <div key={court.name}>
                        <p>
                          <a href={court.url}>{court.name}</a><br />
                          {court.phone}<br />
                          {get(court, 'address.display')}
                        </p>
                        <ImageStaticMap
                          alt={court.name}
                          address={court.address}
                        />
                      </div>
                    ))}

                    <p>Once you enter the doors of the court house, you will be greeted by security guards, who will ask you to pass through a metal detector.  Once you pass security, you will go to the clerk’s office. And you will tell the clerk, “I’d like to file for bankruptcy.” You’ll hand the Clerk your bankruptcy forms, along with your filing fee of $335 in exact change.  Or if you are filing a fee waiver or installment plan, you hand the clerk the fee waiver form or fee installment form instead of the cash.</p>
                    <p>The clerk will take your bankruptcy forms and ask you to take a seat in their waiting room.  It shouldn’t take long for the clerk long to process your case - about 15 minutes.  During this time, they will scan your forms and upload them to the court’s online filing system.  </p>
                    <p>As soon as they are done processing your forms, the clerk will call you back to the front desk.  The clerk will give you:</p>

                    <ul>
                      <li><Link to="/learn/bankruptcy-case-number/">Your bankruptcy case number</Link></li>
                      <li>The name of your <Link to="/learn/what-is-a-bankruptcy-trustee/">bankruptcy trustee</Link></li>
                      <li>The date, time, and location of your meeting with your trustee (this is called the “Meeting of Creditors” or “<Link to="/learn/how-prepare-hearing/">341 Meeting</Link>”)</li>
                    </ul>

                    <p>At this point, your case has been filed! Congrats! Something very important has just happened. Your debt collectors are now legally prohibited by <Link to="/learn/what-is-automatic-stay-bankruptcy/">bankruptcy’s Automatic Stay</Link> from contacting you to collect your debts, from garnishing your wages, or foreclosing on your property. This lasts until the end of your bankruptcy case, at which point most, if not all, of your debts will hopefully be erased.</p>
                    <p>But you’re not home yet - there are other steps you need to complete to get a fresh start!</p>

                    <H3 id="7maildocumentstoyourtrustee">7. Mail Documents to Your Trustee</H3>
                    <p>The bankruptcy trustee is a official appointed by the court to oversee your case. Pay attention to mail you receive from the trustee after filing. The trustee will send you a letter asking you to mail them <Link to="/learn/chapter-7-bankruptcy-forms-explained/">certain financial documents</Link>, like tax returns, pay stubs, and bank statements. If you don’t mail the the trustee the requested documents, you will not get a discharge of your debts.</p>

                    <H3 id="8takebankruptcycourse2">8. Take Bankruptcy Course 2</H3>
                    <p>As soon as possible after filing your bankruptcy forms, you also need to take your second mandatory bankruptcy course. The second course, called the Debtor Education Course, is similar to the credit counseling course. But it is designed to educate you on making smart financial choices so that you won’t have to seek bankruptcy relief in the future.</p>
                    <p>Course 2 can be completed online or by phone and takes at least 2 hours to complete. The fee for the course ranges from $10-$50. But the fee may be waived if your household income is under 150% of the federal poverty level.</p>
                    <p>If you don’t complete the course, you will not obtain a fresh start. So make sure to complete the course as soon as possible after filing.</p>

                    <H3 id="9attendyour341meeting">9. Attend Your 341 Meeting</H3>
                    <p>Finally, you need to attend your 341 meeting. The location of the 341 Meeting depends on where you filed your bankruptcy case.</p>
                    <p>Usually, the 341 Meeting takes place about a month after filing.  The main purpose of the 341 Meeting is to ensure that you are not hiding any expensive assets that should be distributed to creditors. If your papers were done correctly, you should have no trouble answering the questions. Most meetings last only about 5 minutes. Creditors are allowed to attend, although they almost never do.</p>
                    <p>Important note: You must bring your <a href="https://www.justice.gov/sites/default/files/ust-regions/legacy/2011/07/13/proof_of_id_341_meeting.pdf" target="_blank" rel="noopener noreferrer">government-issued ID and social security card</a> to the meeting. If you don’t bring them, the trustee cannot verify your identity and the meeting cannot go forward. You should also bring a copy of your bankruptcy forms to the meeting, along with your last 60 days of pay stubs, your recent bank statements, and any other documents that your trustee has asked for.</p>
                    <p>In most cases, the trustee “closes” the case at the end of the meeting. In that case, unless something very unusual happens, you get a letter two months later from the Court stating that your debts have been discharged.</p>

                    <H3 id="10optionaldealingwithyourcar">10. Optional - Dealing with Your Car</H3>
                    <p>Finally, there’s an additional step in the bankruptcy process if you own a car with outstanding debt. If you want to <Link to="/learn/when-surrender-car/">surrender the car</Link> to the lender, the lender will file a motion with the bankruptcy court to ask permission to retake the car.</p>
                    <p>Alternatively, you might choose to keep the car by <Link to="/learn/reaffirm-car-loan/">“reaffirming” the car debt</Link> and continuing making payments on it. In that case, your lender would normally send you a reaffirmation agreement that you would need to sign and return within 45 days after your 341 meeting. The lender would then file the signed reaffirmation agreement with the court for approval. If the judge approves your reaffirmation, you would get a notice of reaffirmation along with your discharge. And you would be able to keep the vehicle as long as you stay current on your payments.</p>
                    <p>Finally, you may also have chosen <Link to="/learn/how-redeem-car/">“redeem” the car</Link> by buying it back from the lender in one lump sum, usually obtained from a lender like 722redemption.com. If you chose redemption, you will be required to filing a motion in the bankruptcy case.</p>

                    <H3 id="conclusion">Conclusion</H3>
                    <p>Filing for bankruptcy in {startCase(city.city)} takes some careful preparation. Hiring a good bankruptcy attorney is the most convenient way to file.  But if you cannot afford one and you need a fresh start, see if you qualify for Upsolve’s free nonprofit bankruptcy service <a href={screenerUrl}>here</a>.</p>
                    <p>You’ll be glad you did!</p>
                  </section>
                  <CTATestimonial contentGroup={contentGroup} />
                  <section>
                    <H2 id="means-test">Means Test</H2>
                    <P>The Means Test is the document used to determine if a debtor exceeds the Chapter 7 income limits. Here are some numbers that can help you quickly determine your eligibility. If you want to learn more about the Means Test, <Link to="/learn/what-are-the-chapter-seven-income-limits/">read this article</Link>.</P>
                    <H5>Median income levels for {startCase(city.city)}, {STATES[city.state]}</H5>
                    <StateMedianIncomeChart state={city.state} />
                    <H5>Poverty levels for {startCase(city.city)}, {STATES[city.state]}</H5>
                    <StatePovertyLevelsChart state={city.state} />
                  </section>
                  <section>
                    <H2 id="forms">Bankruptcy Forms for {startCase(city.city)}, {STATES[city.state]}</H2>
                    <P>There are about 23 federal forms involved in a bankruptcy filing, not including the local and optional forms that you might need for your case.</P>
                    <P>The Petition is a series of different forms that outline your financial resources and obligations. It is how you show that you are unable to make ends meet. At filing, you will need to present a few other documents including your certificate of financial counseling, your redacted tax returns and pay stubs.</P>
                    <P>For a comprehensive overview of the voluntary petition, A/B, C, D, E/F, G, H, I, J, or other forms read the <Link to={`/${city.state.toLowerCase()}/bankruptcy-forms/`}>{STATES[city.state]} guide for bankruptcy forms</Link>. Below are details for state and local unique forms.</P>
                    <P>If you feeel overwhelmed or want assistance, <a href={screenerUrl}>check out Upsolve's free bankruptcy assistance</a> and see if you’re a good fit for our service. We have a free service that makes the bankruptcy process super simple for residents of {STATES[city.state]}.</P>
                    <H5 as="h3">{city.city}, {state} is within the <u>{district.name}</u></H5>
                    {get(district, 'requirements.guide') != null
                      ? (
                        <div>
                          <P>{districtRequirements.guide}</P>
                        </div>
                      )
                      : (
                        <P>Your district has no special forms!</P>
                      )}
                    <H6>Other Details</H6>
                    <ul>
                      {get(district, 'requirements.copiesKeep') === true && <li>Expect to keep copies of your documents.</li>}
                      {get(district, 'requirements.copiesNumber') > 1
                        ? <li>You will need {get(district, 'requirements.copiesNumber')} copies of your forms.</li>
                        : <li>You will need one copy of your forms.</li>}
                        {get(district, 'requirements.payStubsFiled') === true && <li>Expect to file your paystubs.</li>}
                        {get(district, 'requirements.rideThrough') != null && <li>Ridethrough expectations: {get(district, 'requirements.rideThrough')}</li>}
                        {get(district, 'requirements.petitionMailed') === true && <li>Expect to mail your petition to the trustee.</li>}
                    </ul>
                    {(get(district, 'forms') || []).length > 0 && (
                      <div>
                        <H6>{district.name} Forms</H6>
                        <ul>
                          {Array.prototype.map.call(get(district, 'forms') || [], (form) => form.url != null
                            ? (
                              <li>
                                <a href={form.url} target="_blank" rel="noopener noreferrer">{form.name}{form.mandatory === true ? ' (Mandatory)': ''}</a>
                              </li>
                            ) : (
                              <li>
                                {form.name}{form.mandatory === true ? ' (Mandatory)': ''}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </section>
                  <section>
                    <H2 id="exemptions">{FEDERAL_EXEMPTION_STATES.includes(state) === false ? `${startCase(city.city)}, ${STATES[city.state]} ` : ''}Exemptions</H2>
                    <P>Bankruptcy exemptions protect the equity in certain property. Property that is exempt cannot be used to pay debts. Therefore, a debtor is permitted to keep all exempt property. If you have more questions about exemptions, check out <Link to="/learn/chapter-7-bankruptcy-exemptions/">this article</Link>.</P>
                    {FEDERAL_EXEMPTION_STATES.includes(state) && <P>
                      Many people in {STATES[city.state]} choose to use the federal exemptions instead of {STATES[city.state]} exemptions. We have listed the federal exemptions below.
                    </P>}
                    <P>For expanded descriptions, go to the <Link to={`/${city.state.toLowerCase()}/exemptions/`}>{STATES[city.state]} exemption page</Link>. You will find information about specific statutes and answers to common questions there.</P>
                  </section>
                  <section>
                    <H2 id="cost-estimate">{startCase(city.city)}, {STATES[city.state]} Bankruptcy Attorney Cost</H2>
                    <p><Link to={`/${city.state.toLowerCase()}/${city.city.toLowerCase().replace(/\s/g, '-')}/attorney-cost-estimate/`}>We estimate</Link> that attorneys in {startCase(city.city)}, {STATES[city.state]} cost ${Number(privatePricings[city.state].low).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} &ndash; ${Number(privatePricings[city.state].high).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}. We started Upsolve realizing this was too expensive for Americans, and wanted to give another option with Upsolve's free service. If you are interested, <a href={screenerUrl}>take our screener</a> to see if you qualify for this free service.</p>
                  </section>
                </div>
                <aside className="article__sidebar">
                  <div className="sidebar-hero__cta">
                    <HeroFilerCta contentGroup={contentGroup} />
                  </div>
                </aside>
              </div>
            </article>
          </div>
          <div className="related-content">
            <ContentShareRow text={title} slug={path} />
            <StateCityGuidesLinks {...city} />
          </div>
        </Article>
        <CTASideBySideOfferings contentGroup={contentGroup} ctaText={cta} />
        <MainFooter />
      </TopLevelLayout>
    );

    return amp === true
      ? <Ampify>{children}</Ampify>
      : children;
  }
}
